import type { CSSProperties } from 'react'
import { Button, Card, DateRangeInput, Segmented, type DateRangeInputLabels } from '@grovus/design-system'
import type { Dict, Lang } from '../i18n'
import type { Bounds } from '../creatives/contract'
import { fmt } from '../lib/format'
import { formatRange, isPresetDays, type Period, type PresetDays } from '../lib/period'
import type { PanelProps, TriggerProps } from './useDisclosurePanel'

interface Props {
  t: Dict
  lang: Lang
  period: Period
  onChange: (period: Period) => void
  /** The presets on offer: all five, or the old contract's three while the shell talks to a
   * server that predates the range. */
  presets: readonly PresetDays[]
  /** What the picker may offer, for the current company and accounts. `null` until the
   * scope's first response lands, and always from the old contract: the trigger is then
   * disabled and the presets carry on working. */
  bounds: Bounds | null
  /** Below 768px the picker shows one month instead of two. */
  narrow: boolean
  /** The range picker is a header disclosure panel. App owns the one `useDisclosurePanel`
   * instance, so these come from it and never from a hook of this component's own. */
  rangeOpen?: boolean
  rangeTriggerProps?: TriggerProps
  rangePanelProps?: PanelProps
  closeRange?: () => void
}

/** The calendar's control names, from the dictionary. */
export function calendarLabels(t: Dict): DateRangeInputLabels {
  return {
    previousMonth: t.calPrevMonth, nextMonth: t.calNextMonth,
    previousYear: t.calPrevYear, nextYear: t.calNextYear,
    previousDecade: t.calPrevDecade, nextDecade: t.calNextDecade,
    monthLevelControl: t.calMonthLevel, yearLevelControl: t.calYearLevel,
  }
}

/** A calendar glyph for the range trigger at phone width, where its words would push the
 * header onto a third row. Drawn in the text colour, so it follows both palettes. */
function CalendarGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <rect x="2" y="3" width="12" height="11" rx="1.5" />
      <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" />
    </svg>
  )
}

/**
 * The period control: five presets, each ending on the last complete day, and a range the
 * reader picks on a calendar. The period is the window the store sums the daily rows
 * over, so a change is a refetch and the figures move with it.
 */
export function PeriodControl({
  t, lang, period, onChange, presets, bounds, narrow,
  rangeOpen = false, rangeTriggerProps, rangePanelProps, closeRange,
}: Props) {
  const range = period.kind === 'range' ? period : null
  const canPick = bounds !== null && rangeTriggerProps !== undefined

  return (
    // At phone width the control shares a header row with help and the avatar. The compact
    // sizes and the tighter segment padding keep all three on it with five presets in Russian
    // and Ukrainian at 375 px, so the header stays under its 150 px ceiling (GRO-68, GRO-166).
    <div data-testid="period-control" style={{ display: 'flex', alignItems: 'center', gap: narrow ? 4 : 10, flexWrap: 'wrap' }}>
      <Segmented
        size={narrow ? 'xs' : undefined}
        styles={narrow ? { label: { paddingInline: 3 } } : undefined}
        aria-label={t.period}
        // A range is active: no preset is, so none shows as selected.
        value={period.kind === 'preset' ? String(period.days) : ''}
        onChange={(value) => {
          const days = Number(value)
          if (isPresetDays(days)) onChange({ kind: 'preset', days })
        }}
        data={presets.map((d) => ({ value: String(d), label: fmt(t.periodUnit, { n: d }) }))}
      />
      {rangeTriggerProps && (
        <Button
          size={narrow ? 'compact-md' : undefined}
          px={narrow ? 8 : undefined}
          variant={range ? 'filled' : 'default'}
          disabled={!canPick}
          aria-label={range ? `${t.periodPicker}: ${formatRange(range.from, range.to, lang)}` : t.periodPicker}
          data-testid="period-range-toggle"
          // A disabled button still receives `pointerdown`, and the disclosure's handler would
          // mark the picker open for its bounds to reveal later, unasked and unfocused. Until
          // it can pick, the trigger keeps only its ref and its ARIA wiring.
          {...(canPick
            ? rangeTriggerProps
            : { ref: rangeTriggerProps.ref, 'aria-controls': rangeTriggerProps['aria-controls'], 'aria-expanded': false })}
        >
          {narrow ? <CalendarGlyph /> : range ? formatRange(range.from, range.to, lang) : t.periodRange}
        </Button>
      )}
      {rangeOpen && canPick && rangePanelProps && (
        <Card
          role="group"
          aria-label={t.periodPicker}
          className="header-panel"
          data-testid="period-range-panel"
          style={{ right: 0, '--panel-width': narrow ? '300px' : '580px' } as CSSProperties}
          {...rangePanelProps}
        >
          <DateRangeInput
            value={range ? [range.from, range.to] : null}
            minDate={bounds.earliest}
            maxDate={bounds.today}
            locale={lang}
            numberOfColumns={narrow ? 1 : 2}
            aria-label={t.periodPicker}
            labels={calendarLabels(t)}
            onCommit={([from, to]) => {
              onChange({ kind: 'range', from, to })
              closeRange?.()
            }}
          />
        </Card>
      )}
    </div>
  )
}
